import { fetchClient } from "@/api/api";
import type {
	CancelListingStruckSuggestedPlayerInput,
	CancelListingStruckSuggestedPlayerOutput,
	CancelMultipleListingStruckSuggestedPlayerInput,
	CancelMultipleListingStruckSuggestedPlayerOutput,
	deleteSuggestedPlayerInput,
	deleteSuggestedPlayerOutput,
	getAllSuggestedPlayersInput,
	getAllSuggestedPlayersOutput,
	getSuggestedPlayerInput,
	getSuggestedPlayerOutput,
	getSuggestedPlayersFacetsInput,
	getSuggestedPlayersFacetsOutput,
	updateSuggestedPlayerInput,
	updateSuggestedPlayerOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { createFacetSource } from "@/services/facets";
import type { ApiError } from "@/utils/fetching";
import {
	type CustomMutationHook,
	type CustomQueryHook,
	invalidateQueries,
} from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SUGGESTED_PLAYERS_SERVICE_KEYS } from "./keys";

export const useGetAllSuggestedPlayers: CustomQueryHook<
	getAllSuggestedPlayersOutput,
	getAllSuggestedPlayersInput
> = (params, options) => {
	return useQuery<getAllSuggestedPlayersOutput, ApiError>({
		queryKey: SUGGESTED_PLAYERS_SERVICE_KEYS.useGetAllSuggestedPlayers(params),
		refetchInterval: 10 * 1000,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/suggested-players/",
				{
					params: { query: params },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

export const useGetSuggestedPlayersFacets: CustomQueryHook<
	getSuggestedPlayersFacetsOutput,
	getSuggestedPlayersFacetsInput
> = (params, options) => {
	return useQuery<getSuggestedPlayersFacetsOutput, ApiError>({
		queryKey:
			SUGGESTED_PLAYERS_SERVICE_KEYS.useGetSuggestedPlayersFacets(params),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/suggested-players/facets/",
				{
					params: { query: params },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		...options,
	});
};

export const useGetSuggestedPlayer: CustomQueryHook<
	getSuggestedPlayerOutput,
	getSuggestedPlayerInput
> = (options) => {
	return useQuery({
		...options,
		queryKey: SUGGESTED_PLAYERS_SERVICE_KEYS.useGetSuggestedPlayer(),
		enabled: !!options.suggested_player_id,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/suggested-players/{suggested_player_id}",
				{
					params: {
						path: { suggested_player_id: options.suggested_player_id },
					},
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};

export const useUpdateSuggestedPlayer: CustomMutationHook<
	updateSuggestedPlayerInput,
	updateSuggestedPlayerOutput
> = () => {
	const { toastApiError } = useApiError();
	return useMutation({
		mutationKey: SUGGESTED_PLAYERS_SERVICE_KEYS.useUpdateSuggestedPlayer(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/suggested-players/{suggested_player_id}",
				{
					params: { path: { suggested_player_id: body.resource_id } },
					body,
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useDeleteSuggestedPlayer: CustomMutationHook<
	deleteSuggestedPlayerInput,
	deleteSuggestedPlayerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: SUGGESTED_PLAYERS_SERVICE_KEYS.useDeleteSuggestedPlayer(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/fc/suggested-players/{suggested_player_id}",
				{
					params: {
						path: { suggested_player_id: payload.suggested_player_id },
					},
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useCancelListingStruckSuggestedPlayer: CustomMutationHook<
	CancelListingStruckSuggestedPlayerInput,
	CancelListingStruckSuggestedPlayerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		CancelListingStruckSuggestedPlayerOutput,
		ApiError,
		CancelListingStruckSuggestedPlayerInput
	>({
		mutationKey:
			SUGGESTED_PLAYERS_SERVICE_KEYS.useCancelListingStruckSuggestedPlayer(),

		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/players-auction/suggested/{suggested_player_id}/cancel",
				{
					params: { path: payload },
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(
				SUGGESTED_PLAYERS_SERVICE_KEYS.useGetAllSuggestedPlayers(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useCancelMultipleListingStruckSuggestedPlayers: CustomMutationHook<
	CancelMultipleListingStruckSuggestedPlayerInput,
	CancelMultipleListingStruckSuggestedPlayerOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		CancelMultipleListingStruckSuggestedPlayerOutput,
		ApiError,
		CancelMultipleListingStruckSuggestedPlayerInput
	>({
		mutationKey:
			SUGGESTED_PLAYERS_SERVICE_KEYS.useCancelMultipleListingStruckSuggestedPlayers(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/players-auction/suggested/cancel",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(
				SUGGESTED_PLAYERS_SERVICE_KEYS.useGetAllSuggestedPlayers(),
			);
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const SUGGESTED_PLAYERS_FACET_SOURCE =
	createFacetSource<getSuggestedPlayersFacetsOutput>({
		path: "/v1/fc/suggested-players/facets/",
		queryKey: (query) =>
			SUGGESTED_PLAYERS_SERVICE_KEYS.useGetSuggestedPlayersFacets(query as any),
	});
